/**
 * User Settings Store - Zustand State Management
 * 
 * Manages user profile and preferences state.
 * 
 * @validates Requirements - User settings management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  User,
  UserPreferences,
  UpdateUserPreferencesRequest,
  UpdateEmailRequest,
  UpdatePasswordRequest,
} from '@budgira/shared';
import { PAGE_SIZE_DEFAULT } from '@budgira/shared';
import { userSettingsService } from '../services/user-settings.service';

interface UserSettingsState {
  // Profile state
  profile: User | null;
  
  // Preferences state
  preferences: UserPreferences | null;
  
  // Loading states
  isLoadingProfile: boolean;
  isLoadingPreferences: boolean;
  isSaving: boolean;
  
  // Error state
  error: string | null;
  
  // Actions - Profile
  fetchProfile: () => Promise<void>;
  updateEmail: (data: UpdateEmailRequest) => Promise<boolean>;
  updatePassword: (data: UpdatePasswordRequest) => Promise<boolean>;
  
  // Actions - Preferences
  fetchPreferences: () => Promise<void>;
  updatePreferences: (data: UpdateUserPreferencesRequest) => Promise<boolean>;
  setDefaultAccount: (accountId: string | null) => Promise<boolean>;
  setOperationsPageSize: (pageSize: number) => Promise<boolean>;
  
  // Utility
  clearError: () => void;
  reset: () => void;
}

const DEFAULT_PAGE_SIZE = PAGE_SIZE_DEFAULT;

export const useUserSettingsStore = create<UserSettingsState>()(
  persist(
    (set, get) => ({
      profile: null,
      preferences: null,
      isLoadingProfile: false,
      isLoadingPreferences: false,
      isSaving: false,
      error: null,

      // ============================================
      // PROFILE ACTIONS
      // ============================================

      fetchProfile: async (): Promise<void> => {
        set({ isLoadingProfile: true, error: null });
        try {
          const response = await userSettingsService.getProfile();
          if (response.success && response.data) {
            set({ profile: response.data, isLoadingProfile: false });
          } else {
            set({ error: response.error?.message || 'Failed to fetch profile', isLoadingProfile: false });
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoadingProfile: false });
        }
      },

      updateEmail: async (data: UpdateEmailRequest): Promise<boolean> => {
        set({ isSaving: true, error: null });
        try {
          const response = await userSettingsService.updateEmail(data);
          if (response.success && response.data) {
            set({ profile: response.data, isSaving: false });
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to update email', isSaving: false });
            return false;
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isSaving: false });
          return false;
        }
      },

      updatePassword: async (data: UpdatePasswordRequest): Promise<boolean> => {
        set({ isSaving: true, error: null });
        try {
          const response = await userSettingsService.updatePassword(data);
          if (response.success) {
            set({ isSaving: false });
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to update password', isSaving: false });
            return false;
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isSaving: false });
          return false;
        }
      },

      // ============================================
      // PREFERENCES ACTIONS
      // ============================================

      fetchPreferences: async (): Promise<void> => {
        set({ isLoadingPreferences: true, error: null });
        try {
          const response = await userSettingsService.getPreferences();
          if (response.success && response.data) {
            set({ preferences: response.data, isLoadingPreferences: false });
          } else {
            set({ error: response.error?.message || 'Failed to fetch preferences', isLoadingPreferences: false });
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoadingPreferences: false });
        }
      },

      updatePreferences: async (data: UpdateUserPreferencesRequest): Promise<boolean> => {
        set({ isSaving: true, error: null });
        try {
          const response = await userSettingsService.updatePreferences(data);
          if (response.success && response.data) {
            set({ preferences: response.data, isSaving: false });
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to update preferences', isSaving: false });
            return false;
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isSaving: false });
          return false;
        }
      },

      setDefaultAccount: async (accountId: string | null): Promise<boolean> => {
        return get().updatePreferences({ defaultAccountId: accountId });
      },

      setOperationsPageSize: async (pageSize: number): Promise<boolean> => {
        return get().updatePreferences({ operationsPageSize: pageSize });
      },

      // ============================================
      // UTILITY
      // ============================================

      clearError: () => set({ error: null }),

      reset: () => set({
        profile: null,
        preferences: null,
        isLoadingProfile: false,
        isLoadingPreferences: false,
        isSaving: false,
        error: null,
      }),
    }),
    {
      name: 'budgira-user-settings',
      partialize: (state) => ({
        preferences: state.preferences,
      }),
    }
  )
);

/**
 * Get the user's preferred page size for operations
 * Falls back to default if not set
 */
export function getOperationsPageSize(): number {
  const { preferences } = useUserSettingsStore.getState();
  return preferences?.operationsPageSize || DEFAULT_PAGE_SIZE;
}

/**
 * Get the user's default account ID
 */
export function getDefaultAccountId(): string | undefined {
  const { preferences } = useUserSettingsStore.getState();
  return preferences?.defaultAccountId;
}

export default useUserSettingsStore;
